import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Container,
  Tab,
  Tabs,
  IconButton,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalculateIcon from '@mui/icons-material/Calculate';
import { useParams, useNavigate } from 'react-router-dom';
import LocationTree from '../components/LocationTree';
import LocationDetailForm from '../components/LocationDetailForm';
import Warehouse2DMapEditor from '../components/map/Warehouse2DMapEditor';
import { locationService } from '../services/locationService';
import { smartWarehouseService } from '../services/smartWarehouseService';
import { InventoryService } from '../../accounting/inventory/services/inventoryService';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';

export interface LocationTreeNode {
  Id: string;
  ParentId?: string | null;
  Code: string;
  Name: string;
  Type: string;
  AisleId?: string | null;
  AisleSide?: string | null;
  SortOrder: number;
  GridX?: number | null;
  GridY?: number | null;
  GridWidth?: number | null;
  GridHeight?: number | null;
  Capacity?: number | null;
  IsActive: boolean;
  IsPickable: boolean;
  BarcodeData?: string | null;
  Children: LocationTreeNode[];
}

interface WarehouseInfo {
  id: string;
  name: string;
  locationModel?: string;
  occupancyRatePercent?: number;
  occupiedLocations?: number;
  totalPickableLocations?: number;
}

interface StockByLocationRow {
  id: string;
  productId: string;
  productName: string;
  productBarcode: string;
  quantity: number;
  locationId: string | null;
  locationCode: string;
  locationName: string;
}

const WmsLocationManager: React.FC = () => {
  const { t } = useTranslation('warehouse');
  const { warehouseId } = useParams<{ warehouseId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [warehouse, setWarehouse] = useState<WarehouseInfo | null>(null);
  const [treeData, setTreeData] = useState<LocationTreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [sortOrderLoading, setSortOrderLoading] = useState(false);
  const [occupiedLocationIds, setOccupiedLocationIds] = useState<Set<string>>(new Set());
  const [locationStockRows, setLocationStockRows] = useState<StockByLocationRow[]>([]);
  const [locationStockLoading, setLocationStockLoading] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const loadTree = useCallback(async () => {
    if (!warehouseId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await locationService.getTree(warehouseId);
      const data = response.data?.Data || response.data?.data || [];
      setTreeData(normalizeNodes(data));
    } catch (err) {
      setError(t('pages.locationManager.loadError'));
      console.error('Tree load error:', err);
    } finally {
      setLoading(false);
    }
  }, [warehouseId]);

  const loadWarehouse = useCallback(async () => {
    if (!warehouseId) return;
    try {
      const detail = await smartWarehouseService.getByIdDetail(warehouseId);
      if (detail) {
        setWarehouse({
          id: detail.id,
          name: detail.name,
          locationModel: detail.locationModel,
          occupancyRatePercent: detail.occupancyRatePercent,
          occupiedLocations: detail.occupiedLocations,
          totalPickableLocations: detail.totalPickableLocations,
        });
      }
    } catch {
      // ignore - tree is primary
    }
  }, [warehouseId]);

  const loadOccupiedLocations = useCallback(async () => {
    if (!warehouseId) return;
    try {
      const resp = await InventoryService.getStockByLocation(warehouseId, { page: 1, pageSize: 10000 });
      const items = (resp?.data ?? []) as any[];
      const occupied = new Set(
        items
          .map((row: any) => row.locationId || row.LocationId)
          .filter((id: string | null | undefined): id is string => !!id)
      );
      setOccupiedLocationIds(occupied);
    } catch (err) {
      console.error('Occupied locations load error:', err);
      setOccupiedLocationIds(new Set());
    }
  }, [warehouseId]);

  useEffect(() => {
    loadWarehouse();
    loadTree();
    loadOccupiedLocations();
  }, [loadWarehouse, loadTree, loadOccupiedLocations]);

  const handleCalculateSortOrder = async () => {
    if (!warehouseId) return;
    try {
      setSortOrderLoading(true);
      await locationService.calculateSortOrder(warehouseId);
      showSnackbar(t('pages.locationManager.sortOrderCalculated'));
      await loadTree();
    } catch (err: any) {
      const serverMsg = err?.response?.data?.Message || err?.response?.data?.message;
      showSnackbar(serverMsg || t('pages.locationManager.sortOrderError'), 'error');
    } finally {
      setSortOrderLoading(false);
    }
  };

  const handleDataChanged = useCallback(() => {
    loadTree();
    loadOccupiedLocations();
    loadWarehouse();
  }, [loadTree, loadOccupiedLocations, loadWarehouse]);

  const selectedNode = selectedNodeId ? findNodeById(treeData, selectedNodeId) : null;

  const isMapTab = tabValue === 0;

  useEffect(() => {
    const loadSelectedLocationStocks = async () => {
      if (!warehouseId || !selectedNodeId) {
        setLocationStockRows([]);
        return;
      }

      try {
        setLocationStockLoading(true);
        const resp = await InventoryService.getStockByLocation(warehouseId, {
          locationId: selectedNodeId,
          page: 1,
          pageSize: 10000,
        });
        const rows = (resp?.data ?? []).map((row: any) => ({
          id: row.id || row.Id,
          productId: row.productId || row.ProductId,
          productName: row.productName || row.ProductName || '-',
          productBarcode: row.productBarcode || row.ProductBarcode || '-',
          quantity: Number(row.quantity ?? row.Quantity ?? 0),
          locationId: row.locationId || row.LocationId || null,
          locationCode: row.locationCode || row.LocationCode || '-',
          locationName: row.locationName || row.LocationName || '-',
        })) as StockByLocationRow[];

        setLocationStockRows(rows);
      } catch (err) {
        console.error('Selected location stock load error:', err);
        setLocationStockRows([]);
      } finally {
        setLocationStockLoading(false);
      }
    };

    loadSelectedLocationStocks();
  }, [warehouseId, selectedNodeId]);

  return (
    <Container
      maxWidth={isMapTab ? false : 'xl'}
      disableGutters={isMapTab}
      sx={{
        py: isMapTab ? 0 : 2,
        px: isMapTab ? 1 : undefined,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header — harita sekmesinde kompakt */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: isMapTab ? 0.5 : 2,
        mt: isMapTab ? 0.5 : 0,
        flexWrap: 'wrap',
      }}>
        <IconButton size={isMapTab ? 'small' : 'medium'} onClick={() => navigate('/wms/depo-tanimlama')}>
          <ArrowBackIcon fontSize={isMapTab ? 'small' : 'medium'} />
        </IconButton>
        <Typography
          variant={isMobile || isMapTab ? 'subtitle1' : 'h5'}
          fontWeight="bold"
          sx={{ flex: 1 }}
        >
          {warehouse?.name || t('pages.locationManager.warehouseFallback')} - {t('pages.locationManager.titleSuffix')}
          {/* (i): harita sekmesinde harita editörü rehberi, ağaç sekmesinde lokasyon yönetimi rehberi */}
          <GuideButton
            key={isMapTab ? 'wms.mapEditor' : 'wms.locationManager'}
            guideKey={isMapTab ? 'wms.mapEditor' : 'wms.locationManager'}
            size="small"
            sx={{ ml: 0.5 }}
          />
        </Typography>
        {warehouse?.occupancyRatePercent != null && warehouse.totalPickableLocations != null && (
          <Chip
            size="small"
            color={warehouse.occupancyRatePercent >= 85 ? 'warning' : 'default'}
            variant="outlined"
            label={t('pages.locationManager.occupancyChip', {
              rate: warehouse.occupancyRatePercent,
              occupied: warehouse.occupiedLocations ?? 0,
              total: warehouse.totalPickableLocations,
            })}
            sx={{ fontWeight: 600 }}
          />
        )}
        <Button
          variant="outlined"
          startIcon={sortOrderLoading ? <CircularProgress size={16} /> : <CalculateIcon />}
          onClick={handleCalculateSortOrder}
          disabled={sortOrderLoading || treeData.length === 0}
          size="small"
        >
          {t('pages.locationManager.calculateSortOrder')}
        </Button>
      </Box>

      {/* Tabs — harita sekmesinde alt boşluk minimal */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: isMapTab ? 0.5 : 2 }}>
        <Tabs
          value={tabValue}
          onChange={(_e, v) => setTabValue(v)}
          sx={{ minHeight: isMapTab ? 36 : undefined, '& .MuiTab-root': { minHeight: isMapTab ? 36 : undefined, py: isMapTab ? 0.5 : undefined } }}
        >
          <Tab label={t('pages.locationManager.tabMap2d')} />
          <Tab label={t('pages.locationManager.tabTree')} />
        </Tabs>
      </Box>

      {error && <Alert severity="error" sx={{ mb: isMapTab ? 0.5 : 2 }}>{error}</Alert>}

      {/* Content */}
      {tabValue === 1 && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, overflow: 'hidden', minHeight: 0 }}>
          {/* Left Panel - Tree */}
          <Box sx={{
            width: isMobile ? '100%' : 340,
            minWidth: isMobile ? undefined : 300,
            overflow: 'auto',
            borderRight: isMobile ? 'none' : '1px solid',
            borderBottom: isMobile ? '1px solid' : 'none',
            borderColor: 'divider',
            pr: isMobile ? 0 : 1,
            pb: isMobile ? 1 : 0,
            maxHeight: isMobile ? 400 : undefined,
          }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <LocationTree
                treeData={treeData}
                warehouseId={warehouseId!}
                selectedNodeId={selectedNodeId}
                occupiedLocationIds={occupiedLocationIds}
                onSelect={setSelectedNodeId}
                onDataChanged={handleDataChanged}
                showSnackbar={showSnackbar}
              />
            )}
          </Box>

          {/* Right Panel - Detail */}
          <Box sx={{ flex: 1, overflow: 'auto', pl: isMobile ? 0 : 1 }}>
            {selectedNode ? (
              <>
                <LocationDetailForm
                  node={selectedNode}
                  treeData={treeData}
                  warehouseId={warehouseId!}
                  onDataChanged={handleDataChanged}
                  onDeleted={() => { setSelectedNodeId(null); handleDataChanged(); }}
                  showSnackbar={showSnackbar}
                />

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Raftaki Urunler
                    </Typography>
                    <Chip
                      label={locationStockRows.length > 0 ? 'Dolu Raf' : 'Bos Raf'}
                      size="small"
                      sx={{
                        bgcolor: locationStockRows.length > 0 ? '#ef6c00' : '#2e7d32',
                        color: '#fff',
                        fontWeight: 600,
                      }}
                    />
                  </Box>

                  {locationStockLoading ? (
                    <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={22} />
                    </Box>
                  ) : locationStockRows.length === 0 ? (
                    <Alert severity="success" sx={{ bgcolor: '#e8f5e9', color: '#1b5e20' }}>
                      Bu raf su an bos.
                    </Alert>
                  ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Raf Kodu</TableCell>
                            <TableCell>Raf Adi</TableCell>
                            <TableCell>Urun</TableCell>
                            <TableCell>Barkod</TableCell>
                            <TableCell align="right">Miktar</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {locationStockRows.map((row) => (
                            <TableRow key={row.id}>
                              <TableCell>{row.locationCode}</TableCell>
                              <TableCell>{row.locationName}</TableCell>
                              <TableCell>{row.productName}</TableCell>
                              <TableCell>{row.productBarcode || '-'}</TableCell>
                              <TableCell align="right">{row.quantity}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </Paper>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
                <Typography color="text.secondary">
                  {treeData.length === 0
                    ? t('pages.locationManager.emptyNoLocation')
                    : t('pages.locationManager.emptySelect')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {tabValue === 0 && (
        <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <Warehouse2DMapEditor
            warehouseId={warehouseId!}
            onDataChanged={handleDataChanged}
            showSnackbar={showSnackbar}
          />
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default WmsLocationManager;

function normalizeNodes(data: any[]): LocationTreeNode[] {
  return (data || []).map((n: any) => ({
    Id: n.Id || n.id,
    ParentId: n.ParentId || n.parentId || null,
    Code: n.Code || n.code || '',
    Name: n.Name || n.name || '',
    Type: n.Type || n.type || '',
    AisleId: n.AisleId || n.aisleId || null,
    AisleSide: n.AisleSide || n.aisleSide || null,
    SortOrder: n.SortOrder ?? n.sortOrder ?? 0,
    GridX: n.GridX ?? n.gridX ?? null,
    GridY: n.GridY ?? n.gridY ?? null,
    GridWidth: n.GridWidth ?? n.gridWidth ?? null,
    GridHeight: n.GridHeight ?? n.gridHeight ?? null,
    Capacity: n.Capacity ?? n.capacity ?? null,
    IsActive: n.IsActive ?? n.isActive ?? true,
    IsPickable: n.IsPickable ?? n.isPickable ?? false,
    BarcodeData: n.BarcodeData || n.barcodeData || null,
    Children: normalizeNodes(n.Children || n.children || []),
  }));
}

function findNodeById(nodes: LocationTreeNode[], id: string): LocationTreeNode | null {
  for (const node of nodes) {
    if (node.Id === id) return node;
    const found = findNodeById(node.Children, id);
    if (found) return found;
  }
  return null;
}
